<script setup lang="ts">
import {computed, ref} from "vue";
import BaseInput from "@/core/components/base/BaseInput.vue";
import BaseProductSearchBar from "@/product/components/BaseProductSearchBar.vue";
import {loyaltyCardApi, productApi} from "@/container";
import {currencyze} from "@/core/filters/Currencyze";
import {LoyaltyCardMembership} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import useMembershipProgress from "@/loyalty-card-membership/composables/useMembershipProgress";

export interface ProductCreditValue {
    product_id: number | null;
    quantity: number;
}

const emits = defineEmits<{
    (e: 'update:modelValue', payload: ProductCreditValue): void
}>();

const props = defineProps<{
    modelValue: ProductCreditValue;
    membership: LoyaltyCardMembership;
}>();

const selectedProduct = ref<{ id: number; name: string } | null>(null);
/** Unit price the credit will be valued with, served by the HDS. */
const unitPrice = ref<number | null>(null);
const loadingPrice = ref(false);

const {activeObjective, productThreshold, productProgress, ruleTeaser} = useMembershipProgress(
    computed(() => props.membership)
);

const quantity = computed(() => Math.max(1, props.modelValue.quantity));
const quantityUnit = computed(() => (quantity.value > 1 ? "produits" : "produit"));

const formattedUnitPrice = computed(() => (
    unitPrice.value === null ? null : currencyze(unitPrice.value)
));

/** What the purchase amounts to: unit price times the credited quantity. */
const formattedTotal = computed(() => (
    unitPrice.value === null ? null : currencyze(unitPrice.value * quantity.value)
));

/**
 * The fill the credit will bring the card to.
 *
 * Only projected on a quantity objective: the server credits it with the
 * summed quantities, with no animation coefficient in the way. An amount
 * objective is left alone rather than announcing a progression an animation
 * could multiply.
 */
const projectedFill = computed(() => {
    if (activeObjective.value?.objective?.type?.slug !== "quantity" || productThreshold.value <= 0) {
        return null;
    }

    return productProgress.value + quantity.value;
});

/**
 * How many thresholds the credit crosses. The counter keeps going past the
 * threshold, and stamps that only pay back an advance reach nothing new.
 */
const rewardsGranted = computed(() => (
    projectedFill.value === null
        ? 0
        : Math.floor(projectedFill.value / productThreshold.value)
            - Math.floor(Math.max(0, productProgress.value) / productThreshold.value)
));

/** What the credit will do to the card, in the pharmacist's words. */
const projection = computed(() => {
    if (projectedFill.value === null) {
        return null;
    }

    if (rewardsGranted.value < 1) {
        return `La carte passera à ${projectedFill.value}/${productThreshold.value}.`;
    }

    const times = rewardsGranted.value > 1 ? ` ${rewardsGranted.value} fois` : "";

    return ruleTeaser.value
        ? `Seuil atteint${times} : ${ruleTeaser.value}`
        : `Seuil atteint${times}, la récompense sera créditée.`;
});

/** Search only the products the card would credit: its range and the pharmacy inclusions, minus its exclusions. */
const searchCardProducts = async (query: string) => {
    const response = await loyaltyCardApi().products(props.membership.loyalty_card.id, {search: query, size: 20, type: 'eligible'});

    return response?.data ?? [];
};

const onProduct = async (product: { id: number; name: string }) => {
    selectedProduct.value = {id: product.id, name: product.name};
    emits('update:modelValue', {...props.modelValue, product_id: product.id});

    // Shown for information only: the price is resolved server-side, so what
    // the pharmacist reads here is what the credit will store.
    unitPrice.value = null;
    loadingPrice.value = true;

    try {
        unitPrice.value = await productApi().unitPrice(product.id);
    } catch {
        unitPrice.value = null;
    } finally {
        loadingPrice.value = false;
    }
};
</script>

<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-gray-700">
        Produit
      </label>
      <div class="mt-1">
        <BaseProductSearchBar
          placeholder="Rechercher un produit de la carte…"
          :search-fn="searchCardProducts"
          @select="onProduct"
        />
      </div>
    </div>

    <div class="flex items-end gap-2">
      <BaseInput
        class="w-24"
        :value="modelValue.quantity"
        type="number"
        label="Quantité"
        @input="(value: number) => emits('update:modelValue', {...modelValue, quantity: Number(value) || 1})"
      />
      <span class="text-sm text-slate-500 pb-2.5">{{ quantityUnit }}</span>
    </div>

    <div v-if="selectedProduct" class="rounded-lg bg-primary-lightest p-3 space-y-1">
      <p v-if="loadingPrice" class="text-sm text-slate-500">
        Recherche du prix…
      </p>
      <template v-else-if="formattedTotal">
        <div class="flex justify-between text-sm text-gray-dark">
          <span>Prix unitaire</span>
          <span class="tabular-nums">{{ formattedUnitPrice }}</span>
        </div>
        <div class="flex justify-between text-sm text-gray-dark">
          <span>Total</span>
          <span class="font-semibold text-primary tabular-nums">{{ formattedTotal }}</span>
        </div>
      </template>
      <p v-else class="text-sm text-slate-500">
        Prix indisponible pour ce produit, le crédit sera enregistré sans montant.
      </p>

      <p
        v-if="projection"
        class="text-sm pt-2 mt-2 border-t border-gray-light"
        :class="rewardsGranted >= 1 ? 'text-secondary-darkest font-semibold' : 'text-gray-dark'"
      >
        {{ projection }}
      </p>
    </div>
  </div>
</template>
